// ═══════════════════════════════════════════════════════════════════════════
// Episode Brief generator (v2)
//
// Replaces the multi-section generateEpisode output with a single unified
// "episode brief" document containing: Hook, Interview Questions, and a
// Script (solo) or Talking Points (interview).
//
// Design rules:
//   • ONE API call. No chaining. No post-processing round trips.
//   • System prompt stays under 500 tokens — put the heavy instructions in
//     the user message (cheaper + easier to iterate).
//   • Model is chosen by the user's quality tier:
//       draft    → haiku  (1500 max tokens)
//       standard → sonnet (2500 max tokens)
//       premium  → opus   (4000 max tokens)
//   • Output is typed and minimal so it renders cleanly as a single doc
//     and can be exported straight to PDF / DOCX.
// ═══════════════════════════════════════════════════════════════════════════

import { getVoiceProfile } from './supabase';
import { voiceProfileEpisodeBlock } from './voiceProfileExtractor';

// ─── Quality tiers ────────────────────────────────────────────────────────
// Epio-native names that stay stable as the underlying Claude models
// get updated. The `model` field here is the alias the /api/claude.js
// endpoint routes to — the single source of truth for the actual Claude
// model ID lives in api/claude.js (MODELS map). When Anthropic releases a
// new Haiku/Sonnet/Opus, bumping that one map bumps every tier here
// without any UI churn.
// Mini-spec #57: token caps bumped because the new SYSTEM_PROMPT
// asks for hook_alternates (5–10), title_alternates (4–6),
// reengagement_moments (3–6), and viral-clip markers inline. The
// previous caps were truncating the JSON before script_or_points
// finished, costing a credit on every retry.
// Suno-style version labels — surface the version, not the underlying
// model name. v4.5 is the fast/affordable tier, v4.7 the highest. The
// `model` field still routes to haiku/sonnet/opus internally; the user
// just sees the version number.
export const QUALITY_MODES = {
  draft: {
    model:   'haiku',
    maxTokens: 2400,
    label:   'v4.5',
    tagline: 'Fast + affordable first pass',
    tint:    '#00a8ff',
  },
  standard: {
    model:   'sonnet',
    maxTokens: 4000,
    label:   'v4.6',
    tagline: 'Best for most episodes',
    tint:    '#ffffff',
  },
  premium: {
    model:   'opus',
    maxTokens: 6000,
    label:   'v4.7',
    tagline: 'Maximum quality output',
    tint:    '#ff7a1a',
  },
};

export function normalizeQuality(q) {
  return QUALITY_MODES[q] ? q : 'standard';
}

// SYSTEM PROMPT — episode brief generator. Tightened Nov 2026 per user
// feedback that hooks were "ok but generic". Specificity rules made
// hard requirements (must contain a number, name, or concrete moment),
// the cliché blocklist expanded, and viral-clip / gold-nugget tagging
// made strict so the document is actually usable for clip pulls.
const SYSTEM_PROMPT = `You are Epio's senior producer. You write episode briefs that working podcasters actually use — tight, specific, performance-driven. You think like a top YouTube/podcast strategist, not a content mill.

Return ONE valid JSON object. No preamble, no markdown code fences, no trailing text.

SHAPE
{
  "title": "6–11 words, benefit-led, scroll-stopping. Must contain a number, a proper noun, or a concrete claim.",
  "title_alternates": ["4–6 alternates, each a distinct shape: declarative, numbered, contrarian, possessive, question. Each must reference something specific from the topic."],
  "hook": "1–2 sentences. The lead hook. Specifies WHO, WHAT, or WHEN — not abstract setup.",
  "hook_alternates": ["5–10 cold-open hooks. Pattern variety MANDATORY: provocative claim, in-medias-res scene, data drop, contrarian take, direct second-person question, insider admission, list promise, identity callout. EACH hook must contain at least one of: a number, a proper noun, a place, a time marker, or a specific quoted phrase."],
  "reengagement_moments": ["3–6 mid-episode 'stay-with-me' lines. Each one teases a SPECIFIC upcoming reveal — 'wait until you hear what she did at the airport', not 'we'll get to that'. Anchored at ~25%, 50%, 75% of the runtime."],
  "questions": ["INTERVIEW ONLY: 8–12 questions. EVERY question phrased as a hook — sharp, specific, leading with stakes. Second person ('when did you know...', 'what was the moment...'). NEVER 'tell me about...' or 'how did you get into...'."],
  "script_or_points": "Markdown. SOLO: full script with sections — Cold Open, Intro, 3–5 Main Body beats (each beat opens with a re-engagement line and contains a story or example with names/numbers), Close, CTA. INTERVIEW: talking points grouped by theme with segment timing.\\n\\nMark exactly 2–4 [VIRAL CLIP] lines inline (most-clippable single-take moments). Mark exactly 1–2 [GOLD NUGGET] lines (the quote-worthy teaching moment). These tags drive the clip-pull workflow downstream — don't skip them."
}

SPECIFICITY (HARD RULE)
Every hook, title, and re-engagement moment must contain at least one of: a number, a proper noun, a place, a time marker (year, season, "at 3am"), or a specific quoted phrase. Generic abstractions ("a powerful insight", "a game-changing approach") are forbidden — replace with the concrete thing.

VOICE
Confident, plain-spoken, the way a top human producer talks. Match the tone style provided in the user message exactly — that's not a suggestion.

ANTI-CLICHÉ (NEVER USE)
"delve", "navigate", "in today's fast-paced", "imagine if", "what if I told you", "absolutely", "game-changer", "deep dive", "unpack", "let's dive in", "join me as we", "in this episode we'll explore", "get ready to", "buckle up". No em-dashes (—). No exclamation walls.

If you'd write any of those, write the specific thing instead.`;

// Tone descriptors that shape the actual prose style. Mapped to
// concrete writing instructions so the model can't ignore the chip
// pick. The keys here MUST match the chip ids in BuilderDialIns
// (TONE_CHIPS) so a click → consistent prose feel.
const TONE_INSTRUCTIONS = {
  conversational: 'Write like a friend leaning across a kitchen table. Contractions, second person, asides. Loose sentence rhythm.',
  storytelling:   'Open in a scene. Anchor every beat to a moment, a person, a quote. Use sensory detail and tense pivots.',
  'hard-hitting': 'Lead with the spike. Short declarative sentences. Take a position and defend it. Zero hedging.',
  inspiring:      'Lift. Frame stakes as transformation, every moment as a turning point. End every section on momentum.',
  calm:           'Slow it down. Long sentences, measured pacing, no exclamation points. The host is the steady voice in the storm.',
  'high-energy':  'Punch. Tight beats, exclamation only when earned, bold contrasts. Move the listener forward every line.',
  witty:          'Dry humor, smart asides, unexpected angles. Don\'t mug for the joke — let it land in the structure.',
  documentary:    'Reportorial. Data, names, dates, primary sources. Third-person observational layer over the host\'s voice.',
  reflective:     'Inward-pointing. Pause to ask the listener what THIS means for them. First-person plural where it lands.',
  urgent:         'Time-sensitive framing. "Right now," "this week," "before it\'s too late." Stakes in the present tense.',
};

function buildUserMessage({ topic, format, guest, niche, audience, tone, duration, context, showName, personalization }) {
  const isInterview = format === 'interview' || !!guest;
  const lines = [];
  lines.push(`Topic: ${topic}`);
  if (showName) lines.push(`Show: ${showName}`);
  if (niche)    lines.push(`Niche: ${niche}`);
  if (audience) lines.push(`Audience: ${audience}`);
  if (tone)     lines.push(`Tone: ${tone}`);
  const durMin = Number.parseInt(duration, 10);
  const hasDuration = Number.isFinite(durMin) && durMin > 0;
  const targetWords = hasDuration ? Math.round(durMin * 150) : 0; // ~150 wpm spoken cadence
  const minWords = Math.round(targetWords * 0.9);
  const maxWords = Math.round(targetWords * 1.1);
  if (hasDuration) lines.push(`Target length: ${durMin} min`);
  lines.push(`Format: ${isInterview ? 'interview' : 'solo'}`);
  if (isInterview && guest) lines.push(`Guest: ${guest}`);
  if (context) lines.push(`\nAdditional context:\n${context}`);
  lines.push('');
  // MANDATORY tone block — the system prompt's "Voice" line was too
  // generic ("confident, plain-spoken"), so the model was ignoring
  // the user's tone pick. This makes the tone a hard requirement
  // applied to EVERY output field (title, hook, script, questions).
  if (tone && TONE_INSTRUCTIONS[tone]) {
    lines.push('TONE — MANDATORY. This shapes every field in the output (title, hook_alternates, script_or_points, questions). Do not default to a generic confident voice.');
    lines.push(`Tone style "${tone}": ${TONE_INSTRUCTIONS[tone]}`);
    lines.push('');
  }
  // CREATOR VOICE — the learned voice profile (how they talk, their
  // vocabulary, recurring themes, and boundaries). Placed right after tone
  // so voice + tone shape every field together. Empty for new creators.
  if (personalization) {
    lines.push(personalization);
    lines.push('');
  }
  // LENGTH — MANDATORY. Without a hard word budget the model sizes every
  // script to its own default (~45 min) and ignores the requested
  // runtime. Same enforcement pattern as short-form.js: convert minutes
  // to a word band at spoken cadence and forbid overrun/underrun.
  if (hasDuration) {
    lines.push(`LENGTH — MANDATORY. Hard constraint, not a suggestion. The host is recording a ${durMin}-minute episode.`);
    lines.push(isInterview
      ? `Size the talking points and segment timings so the segment timings SUM TO ~${durMin} minutes total. Do not lay out a longer runway than ${durMin} min.`
      : `The full spoken script — Cold Open + Intro + every Main Body beat + Close + CTA combined — MUST total ${minWords}–${maxWords} words (≈${durMin} min at 150 wpm). Do NOT overrun ${maxWords} words and do NOT come in under ${minWords}. Scale the NUMBER and length of Main Body beats to hit this budget — a ${durMin}-min episode is not the same size as a 45-min one.`);
    lines.push('');
  }
  lines.push(isInterview
    ? 'Format: INTERVIEW. Write 8–12 questions, EVERY ONE phrased as a hook (sharp, leading with stakes, second-person). Then talking points grouped by theme + segment timing. Mark 2–4 [VIRAL CLIP] moments inline + 1–2 [GOLD NUGGET] teaching beats.'
    : 'Format: SOLO. Write a full script with Cold Open, Intro, Main Body beats (as many as the target length needs — fewer for a short episode, more for a long one), Close, and a CTA. Drop the reengagement_moments lines between Main Body beats so the host has stay-with-me beats at ~25/50/75% marks. Include spoken lines, not bullet outlines. Mark 2–4 [VIRAL CLIP] lines inline (the spots clipped for shorts) + 1–2 [GOLD NUGGET] teaching lines.');
  lines.push('');
  lines.push('Output ONLY the JSON object described in the system instructions.');
  return lines.join('\n');
}

/**
 * Turn whatever Claude sent back into a parsed JSON object, tolerating:
 *   - ```json ... ``` fences (closed or unclosed)
 *   - leading prose before the JSON
 *   - trailing prose / a stray closing fence after the JSON
 *   - truncated responses (missing closing braces) — auto-closes them
 *   - trailing commas, bare newlines in string values
 * Throwing here burns a credit on every retry, so we try hard to salvage.
 */
function extractJson(text) {
  if (!text) throw new Error('Empty response from AI. Please try again.');

  // Strip every markdown fence token anywhere in the text. Works whether the
  // closing ``` exists (normal case) or not (truncated output).
  const stripped = text
    .replace(/```json\s*/gi, '')
    .replace(/```/g, '')
    .trim();

  const start = stripped.indexOf('{');
  if (start === -1) throw new Error('No JSON found in response. Please try again.');

  const lastBrace = stripped.lastIndexOf('}');
  const candidates = [];
  if (lastBrace > start) candidates.push(stripped.slice(start, lastBrace + 1));
  candidates.push(autoCloseJSON(stripped.slice(start)));

  for (const candidate of candidates) {
    try { return JSON.parse(candidate); } catch {}
    try {
      const fixed = candidate
        .replace(/,\s*([}\]])/g, '$1')        // trailing commas
        .replace(/:\s*'([^']*)'/g, ': "$1"')  // single → double quotes on values
        // eslint-disable-next-line no-control-regex
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, ''); // stray control chars
      return JSON.parse(fixed);
    } catch {}
  }

  console.error('extractJson failed. Raw preview:', text.slice(0, 800));
  throw new Error('Could not parse AI response. Please try again.');
}

/**
 * Close any unbalanced `{`, `[`, or open string in a truncated JSON snippet.
 * Scans once, ignoring characters inside strings so braces/brackets in values
 * don't throw off the count. Safe to run on already-valid JSON (no-op).
 */
function autoCloseJSON(s) {
  const stack = [];
  let inString = false;
  let escaped = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (escaped) { escaped = false; continue; }
    if (ch === '\\' && inString) { escaped = true; continue; }
    if (ch === '"') { inString = !inString; continue; }
    if (inString) continue;
    if (ch === '{' || ch === '[') stack.push(ch);
    else if (ch === '}' && stack[stack.length - 1] === '{') stack.pop();
    else if (ch === ']' && stack[stack.length - 1] === '[') stack.pop();
  }
  let out = s;
  if (inString) out += '"';
  // Drop a dangling trailing comma before we close containers.
  out = out.replace(/,\s*$/, '');
  while (stack.length) {
    const open = stack.pop();
    out += open === '{' ? '}' : ']';
  }
  return out;
}

/**
 * Generate the unified episode brief. Returns a `document` object ready
 * to persist into `episodes.result.document` and render in the
 * EpisodeDocumentView component.
 */
export async function generateEpisodeBrief(brief, profile, quality = 'standard') {
  const tier = normalizeQuality(quality);
  const { model, maxTokens } = QUALITY_MODES[tier];

  // Personalization: pull the creator's learned voice profile (how they talk,
  // their vocabulary, signature moves, boundaries) and profile memory (themes
  // they return to) so the episode sounds like THEM. Learned in chat, now fed
  // into generation too, so it sharpens with every episode. Fail-soft: any
  // error or a brand-new creator → '' → the brief is unchanged.
  let personalization = '';
  try {
    const vp = profile?.id ? await getVoiceProfile(profile.id) : null;
    personalization = voiceProfileEpisodeBlock(vp, profile);
  } catch { personalization = ''; }

  const userMsg = buildUserMessage({
    topic:     (brief.topic || '').trim(),
    format:    brief.contentType || 'solo',
    guest:     brief.guest || '',
    niche:     brief.niche || profile?.niche || '',
    audience:  brief.audience || profile?.target_audience || '',
    tone:      brief.tone || '',
    duration:  brief.duration || '',
    context:   brief.context || '',
    showName:  brief.show_name || profile?.show_name || '',
    personalization,
  });

  // Stream the response. Episode briefs at sonnet/opus tiers regularly
  // run 30–60s; non-stream calls hit Vercel's 60s function cap before
  // the model finishes, so we use SSE so the connection stays active
  // and we accumulate text as it arrives.
  const res = await fetch('/api/claude', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model,
      max_tokens: maxTokens,
      system: SYSTEM_PROMPT,
      messages: [{ role: 'user', content: userMsg }],
      user_id: profile?.user_id || null,
      stream: true,
    }),
  });

  if (!res.ok) {
    let msg = 'Episode generation failed.';
    try { msg = (await res.json())?.error || msg; } catch {}
    throw new Error(msg);
  }

  const reader  = res.body.getReader();
  const decoder = new TextDecoder();
  let fullText = '';
  let buf = '';
  // streamDone breaks the outer read loop the moment the server emits
  // its final `{done:true,text:...}` marker. Without this we'd keep
  // calling reader.read() — usually it returns done immediately, but if
  // the connection is held open by an upstream proxy or logUsage call
  // the read can hang and the user sees the build "start then stop".
  let streamDone = false;
  while (!streamDone) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split('\n');
    buf = lines.pop();
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const payload = line.slice(6).trim();
      if (!payload) continue;
      try {
        const evt = JSON.parse(payload);
        if (evt.done && typeof evt.text === 'string') {
          fullText  = evt.text;
          streamDone = true;
          break;
        }
        if (typeof evt.delta === 'string') fullText += evt.delta;
        if (evt.error) {
          if (evt.error === 'generation_timeout') {
            throw new Error('Episode generation timed out. Try a shorter brief or drop to a faster quality tier.');
          }
          throw new Error(evt.error);
        }
      } catch (parseErr) {
        if (parseErr.message && !parseErr.message.includes('JSON')) throw parseErr;
        // skip malformed SSE lines
      }
    }
  }
  // Always cancel the reader so a held-open connection doesn't keep
  // the request alive after we have the full text.
  try { reader.cancel(); } catch {}
  if (!fullText) {
    throw new Error('Episode generation returned no content. Please try again.');
  }
  const parsed = extractJson(fullText);

  const isInterview = (brief.contentType === 'interview') || !!brief.guest;

  return {
    title:                 parsed.title || brief.topic || 'Untitled episode',
    title_alternates:      Array.isArray(parsed.title_alternates) ? parsed.title_alternates.filter(Boolean) : [],
    hook:                  parsed.hook || '',
    hook_alternates:       Array.isArray(parsed.hook_alternates) ? parsed.hook_alternates.filter(Boolean) : [],
    reengagement_moments:  Array.isArray(parsed.reengagement_moments) ? parsed.reengagement_moments.filter(Boolean) : [],
    questions:             Array.isArray(parsed.questions) ? parsed.questions.filter(Boolean) : [],
    script:                !isInterview ? (parsed.script_or_points || '') : '',
    talking_points:        isInterview  ? splitTalkingPoints(parsed.script_or_points || '') : [],
    format:                isInterview ? 'interview' : 'solo',
    quality:               tier,
    model_used:            model,
    generated_at:          new Date().toISOString(),
  };
}

/**
 * Interview talking points come back as a markdown blob. Turn it into a
 * list we can render as a clean section in the document without losing
 * the theme grouping.
 */
function splitTalkingPoints(md) {
  if (!md) return [];
  return md
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean);
}
